// Personalização em tempo real via WebSocket — port fiel do initWebSocket/sendWsMessage/
// collectSlots/applyPersonalization/sanitizeHtml do tracker.ts v1. Adaptadores injetáveis
// (socketFactory + dom) = testável em Node. O socket e o DOM são de navegador; a máquina de estado
// (fila, hello, reconnect, roteamento) é agnóstica. Agnóstico (REGRA #1).

import { comoHtml } from "./conteudo.js";

/**
 * O caminho da página atual, quando há uma. É o que permite ao tenant dizer em QUAIS páginas um
 * conteúdo vale — sem isso ele é obrigado a inventar um nome de slot por página.
 *
 * Vive aqui, e os DOIS transportes o usam (este módulo pelo WebSocket, o `tracker` pelo HTTP),
 * porque "que página é esta" precisa ter uma resposta só: com duas, um transporte mandaria o
 * caminho com barra final e o outro sem, e o mesmo conteúdo apareceria num e não no outro.
 *
 * Só o `pathname`: querystring e âncora não fazem parte de "que página é esta", e mandá-las
 * colocaria em trânsito coisas que não deviam sair da barra de endereço (cupom, token, `?email=`).
 * Fora do navegador devolve vazio, e o servidor trata ausência como ausência — nunca como acerto.
 */
export function paginaAtual() {
  try {
    return (typeof location !== "undefined" && location && location.pathname) || "";
  } catch {
    return "";
  }
}

const DANGEROUS_TAGS = ["iframe", "object", "embed", "form", "input", "textarea", "select", "button"];

/** Sanitiza HTML contra XSS. Path DOM no navegador (v1 verbatim); path regex no server/teste. */
export function sanitizeHtml(html) {
  const s = String(html == null ? "" : html);
  if (typeof document !== "undefined" && document.createElement) {
    const temp = document.createElement("div");
    temp.innerHTML = s;
    temp.querySelectorAll("script").forEach((el) => el.remove());
    const dangerousVals = ["javascript:", "data:", "vbscript:"];
    temp.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const n = attr.name.toLowerCase();
        const v = attr.value.toLowerCase();
        if (n.startsWith("on") || dangerousVals.some((d) => v.includes(d))) el.removeAttribute(attr.name);
      });
      if (DANGEROUS_TAGS.includes(el.tagName.toLowerCase())) el.remove();
    });
    return temp.innerHTML;
  }
  return s
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(javascript|data|vbscript)\s*:/gi, "")
    .replace(/<\/?(?:iframe|object|embed|form|input|textarea|select|button)\b[^>]*>/gi, "");
}

/** Adaptador de DOM de navegador: descobre slots e aplica o conteúdo personalizado. */
export function browserDom() {
  return {
    collectSlots() {
      if (typeof document === "undefined") return [];
      const set = new Set();
      document.querySelectorAll("[data-synqi3-slot]").forEach((el) => {
        const name = el.getAttribute("data-synqi3-slot");
        if (name) set.add(name);
      });
      return Array.from(set);
    },
    applySlot(slot, { html } = {}) {
      if (typeof document === "undefined" || !slot) return;
      const safe = String(slot).replace(/[^\w-]/g, "");   // evita injeção no seletor
      if (!safe || html == null) return;
      const clean = sanitizeHtml(html);
      document.querySelectorAll(`[data-synqi3-slot="${safe}"]`).forEach((el) => { el.innerHTML = clean; });
    },

    /**
     * Avisa quando alguém CLICA dentro de um lugar personalizado.
     *
     * Delegado no documento, e uma vez só: pendurar um ouvinte por elemento vazaria a cada
     * reaplicação, e o conteúdo é reaplicado a cada visita.
     *
     * O clique só conta para slot que foi REALMENTE aplicado. Contar clique de um lugar que
     * mostrou o conteúdo da própria página atribuiria à personalização um efeito que ela não teve —
     * e um número que superestima é pior que número nenhum, porque parece medição.
     */
    onSlotClick(handler) {
      if (typeof document === "undefined" || !handler) return () => {};
      const ouvir = (ev) => {
        const alvo = ev.target && ev.target.closest && ev.target.closest("[data-synqi3-slot]");
        if (!alvo) return;
        const slot = alvo.getAttribute("data-synqi3-slot");
        if (!slot) return;
        const cta = ev.target.closest("[data-synqi3-cta]");
        handler(slot, { cta: cta ? cta.getAttribute("data-synqi3-cta") : null });
      };
      document.addEventListener("click", ouvir, true);
      return () => document.removeEventListener("click", ouvir, true);
    },

    /**
     * Quanto da página já foi vista, em %. Leitura pura, para o tracker decidir o marco.
     *
     * A conta é `(topo + altura da janela) / altura do documento` — inclui o que está VISÍVEL, e
     * não só o que passou por cima. Sem isso, uma página que cabe inteira na tela nunca passa de
     * 0%, e "leu tudo" ficaria indistinguível de "não rolou".
     */
    profundidadeVisivel() {
      if (typeof document === "undefined" || typeof window === "undefined") return 0;
      const doc = document.documentElement, corpo = document.body;
      const topo = window.scrollY || doc.scrollTop || corpo.scrollTop || 0;
      const altura = Math.max(corpo.scrollHeight, doc.scrollHeight,
                              corpo.offsetHeight, doc.offsetHeight);
      if (!altura) return 0;
      return Math.round(((topo + window.innerHeight) / altura) * 100);
    },

    /**
     * A página está À VISTA? Aba de fundo e janela minimizada respondem `false`.
     *
     * Sem `document` responde `true`: é o comportamento de antes desta leitura existir, e um
     * runtime sem página não tem aba para estar escondida.
     */
    visivel() {
      if (typeof document === "undefined") return true;
      return document.visibilityState !== "hidden";
    },

    onScroll(handler) {
      if (typeof window === "undefined" || !handler) return () => {};
      const ouvir = () => handler();
      window.addEventListener("scroll", ouvir, { passive: true });
      return () => window.removeEventListener("scroll", ouvir);
    },
  };
}

/**
 * O que renderizar de um slot resolvido, ou `null` — e `null` NÃO substitui nada.
 *
 * `content.html` é o do operador publicando marcação pronta. O resto é a MESMA pergunta que o
 * `textoDoSlot` já responde, e por isso ela é feita a ele: dois lugares decidindo "qual campo é o
 * texto" fariam a mesma variante aparecer diferente conforme o transporte.
 */
function _htmlDoSlot(dado) {
  return comoHtml(dado);
}

export class PersonalizationClient {
  constructor(o) {
    this.wsUrl = o.wsUrl;
    this.tenantId = o.tenantId;
    this.getUserId = o.getUserId || (() => o.userId || "");
    this.sessionId = o.sessionId;
    this.sdkToken = o.sdkToken;
    this.socketFactory = o.socketFactory || ((url) => new WebSocket(url));
    this.dom = o.dom || browserDom();
    this.autoApply = o.autoApply !== false;
    this.onPersonalization = o.onPersonalization;
    this.onMessage = o.onMessage;
    this.maxReconnectDelayMs = o.maxReconnectDelayMs || 30000;
    this.setTimer = o.setTimer || ((fn, ms) => setTimeout(fn, ms));
    this.ws = null;
    this.connected = false;
    this.connecting = false;
    this.queue = [];
    this.reconnectAttempts = 0;
  }

  connect() {
    if (this.connecting || this.connected || !this.wsUrl) return;
    this.connecting = true;
    const ws = this.socketFactory(this._authUrl());
    this.ws = ws;
    ws.onopen = () => {
      this.connecting = false;
      this.connected = true;
      this.reconnectAttempts = 0;
      while (this.queue.length) this._raw(this.queue.shift());
      const slots = this.dom.collectSlots();
      this._raw({ kind: "hello", tenant_id: this.tenantId, user_id: this._uid(),
                  session_id: this.sessionId, capabilities: { personalization: true }, slots });
      if (slots.length) this.requestPersonalization(slots);
    };
    ws.onclose = () => { this.connected = false; this.connecting = false; this._reconnect(); };
    ws.onerror = () => {};
    ws.onmessage = (ev) => this._handle(ev && ev.data);
  }

  sendMessage(msg) {
    if (!this.connected || !this.ws) {
      this.queue.push(msg);
      this.connect();
      return;
    }
    this._raw(msg);
  }

  /** Pede personalização para os slots (descobre no DOM se não vierem). */
  requestPersonalization(slots) {
    const s = slots || this.dom.collectSlots();
    if (!s.length) return;
    this.sendMessage({ kind: "personalization_request", tenant_id: this.tenantId,
                       user_id: this._uid(), session_id: this.sessionId, slots: s,
                       // Mesma chave que a rota HTTP: um contrato, dois transportes.
                       ...(this._pagina() ? { page: this._pagina() } : {}) });
  }

  _authUrl() {
    const u = new URL(this.wsUrl);
    u.searchParams.set("tenant_id", this.tenantId);
    u.searchParams.set("user_id", this._uid());
    if (this.sdkToken) u.searchParams.set("sdk_token", this.sdkToken);
    return u.toString();
  }

  _uid() {
    return this.getUserId() || "";
  }

  _pagina() {
    return paginaAtual();
  }

  _raw(msg) {
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      this.queue.push(msg);
    }
  }

  _handle(data) {
    let msg;
    try {
      msg = JSON.parse(data);
    } catch {
      return;
    }
    if (this.onMessage) this.onMessage(msg);
    if (msg && msg.kind === "personalization") {
      if (this.onPersonalization) this.onPersonalization(msg);
      if (this.autoApply) this._aplicar(msg);
    }
  }

  /**
   * DUAS MENSAGENS COM O MESMO `kind`, e é por isso que este método existe.
   *
   *     resposta ao `personalization_request`   `slots: {nome: {content, variant, ...}}`
   *     push do operador publicando à mão       `slot: "nome", html, data`
   *
   * A primeira é a rota `$default` do WebSocket (`ws_message`); a segunda é o
   * `POST /personalization/publish` saindo pelo SNS. Este cliente lia SÓ a segunda: medido em
   * 21/08/2026, a rota `$default` respondia e o SDK descartava a resposta calado, porque `msg.slot`
   * (singular) nunca existe nela. Cada lado tinha teste, e nenhum teste cruzava os dois.
   *
   * O defeito de fundo é o `kind` compartilhado por duas mensagens diferentes, e ele fica anotado:
   * o certo é a resposta a um pedido ter nome próprio. Enquanto os dois produtores estiverem vivos
   * em produção, este cliente entende os dois — deixar um deles sem consumidor é o que já custou.
   */
  _aplicar(msg) {
    if (msg.slots && typeof msg.slots === "object") {
      // A MESMA forma do `resolveSlots` e do `decisao.js`: quem renderiza não deve saber por qual
      // transporte a decisão chegou.
      for (const [slot, dado] of Object.entries(msg.slots)) {
        const html = _htmlDoSlot(dado);
        if (html != null) this.dom.applySlot(slot, { html, data: dado });
      }
      return;
    }
    if (msg.slot) this.dom.applySlot(msg.slot, { html: msg.html, data: msg.data });
  }

  _reconnect() {
    this.reconnectAttempts += 1;
    const backoff = Math.min(this.maxReconnectDelayMs, 1000 * 2 ** this.reconnectAttempts);
    this.setTimer(() => this.connect(), backoff);
  }
}
